/**
 * /services/:slug — public detail for one memorial service request.
 * Contact info is gated behind an "I want to help" reveal (login-required).
 */
import { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { http } from "@/lib/api";
import { toast } from "sonner";
import { useAuth } from "@/contexts/AuthContext";
import {
  Heart, MapPin, Calendar, Mail, Phone, User, ArrowLeft, Lock, HandHeart, Copy,
} from "lucide-react";

export default function ServiceDetail() {
  const { slug } = useParams();
  const { user } = useAuth();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    setLoading(true);
    http.get(`/services/${slug}`)
      .then((r) => setData(r.data))
      .catch(() => setData({ service: null }))
      .finally(() => setLoading(false));
  }, [slug]);

  if (loading) return <div className="max-w-4xl mx-auto p-16 text-center opacity-70">Loading…</div>;
  const s = data?.service;
  if (!s) {
    return (
      <div className="max-w-3xl mx-auto p-16 text-center">
        <h1 className="font-display text-3xl mb-4">Request not found.</h1>
        <Link to="/services" className="wofy-btn-primary inline-flex">
          <ArrowLeft className="w-4 h-4" /> Back to services
        </Link>
      </div>
    );
  }

  const contactHidden = data?.contact_hidden;
  const copy = async (v) => {
    try { await navigator.clipboard.writeText(v); toast.success("Copied"); }
    catch { toast.error("Copy failed"); }
  };

  return (
    <div data-testid="service-detail-page" className="max-w-[1050px] mx-auto px-6 lg:px-10 py-14">
      <Link to="/services" className="text-xs opacity-70 hover:opacity-100 inline-flex items-center gap-1 mb-6">
        <ArrowLeft className="w-3 h-3" /> All requests
      </Link>

      <div className="overline mb-2">{s.category}{s.subcategory ? ` · ${s.subcategory}` : ""}</div>
      <h1 className="font-display text-4xl md:text-5xl leading-tight">{s.title}</h1>
      {[s.memorial_city, s.memorial_state, s.memorial_country].filter(Boolean).length > 0 && (
        <div className="mt-3 inline-flex items-center gap-1 text-sm opacity-70">
          <MapPin className="w-4 h-4" />
          {[s.memorial_city, s.memorial_state, s.memorial_country].filter(Boolean).join(", ")}
        </div>
      )}

      {s.cover_image && (
        <img src={s.cover_image} alt="" className="mt-6 w-full max-h-[420px] object-cover rounded-2xl" />
      )}

      <div className="mt-8 grid gap-8 md:grid-cols-[1fr_320px]">
        {/* Main body */}
        <div className="space-y-8">
          <section className="wofy-card">
            <div className="overline mb-2">SUMMARY</div>
            <p className="text-base leading-relaxed">{s.short_description}</p>
            {s.detailed_description && (
              <p className="mt-4 text-sm leading-relaxed whitespace-pre-line" style={{ color: "var(--wofy-text-soft)" }}>
                {s.detailed_description}
              </p>
            )}
          </section>

          {/* Loved one */}
          {(s.loved_one_full_name || s.loved_one_native_place || s.loved_one_year_of_birth) && (
            <section className="wofy-card">
              <div className="overline mb-3 inline-flex items-center gap-1.5">
                <Heart className="w-3.5 h-3.5" style={{ color: "var(--wofy-primary)" }} />
                ABOUT THE LOVED ONE
              </div>
              <dl className="grid sm:grid-cols-2 gap-4 text-sm">
                <Row label="Full name" v={s.loved_one_full_name} />
                <Row label="Family name" v={s.loved_one_family_name} />
                <Row label="Year of birth" v={s.loved_one_year_of_birth} />
                <Row label="Year of passing" v={s.loved_one_year_of_death} />
                <Row label="Native place" v={s.loved_one_native_place} />
                <Row label="Siblings" v={s.siblings} />
                <Row label="Requester's relationship" v={s.relationship_to_loved_one} span />
              </dl>
            </section>
          )}

          {/* Memorial site */}
          {(s.memorial_type || s.memorial_notes) && (
            <section className="wofy-card">
              <div className="overline mb-3 inline-flex items-center gap-1.5">
                <MapPin className="w-3.5 h-3.5" style={{ color: "var(--wofy-primary)" }} />
                THE SITE
              </div>
              <dl className="grid sm:grid-cols-2 gap-4 text-sm">
                <Row label="Site type" v={s.memorial_type} />
                <Row label="Full address" v={s.memorial_address} sensitive />
                <Row label="GPS coordinates" v={s.memorial_gps_coords} sensitive />
                <Row label="Notes" v={s.memorial_notes} span />
              </dl>
            </section>
          )}

          {/* Gallery */}
          {(s.images || []).length > 0 && (
            <section className="wofy-card">
              <div className="overline mb-3">PHOTOS</div>
              <div className="grid grid-cols-3 gap-2">
                {s.images.map((img, i) => (
                  <img key={i} src={img} alt="" className="w-full aspect-square object-cover rounded-lg" />
                ))}
              </div>
            </section>
          )}
        </div>

        {/* Sidebar */}
        <aside className="space-y-5">
          {/* Contact card */}
          <div className="wofy-card">
            <div className="overline mb-2 inline-flex items-center gap-1.5">
              <HandHeart className="w-3.5 h-3.5" style={{ color: "var(--wofy-primary)" }} />
              WANT TO HELP?
            </div>

            {contactHidden ? (
              <div>
                <div className="flex items-center gap-2 text-sm mb-3">
                  <Lock className="w-4 h-4 opacity-60" />
                  <span>Sign in to see the requester's contact details.</span>
                </div>
                <Link to={`/login?next=/services/${s.slug}`} className="wofy-btn-primary w-full justify-center" data-testid="service-signin-cta">
                  Sign in to help
                </Link>
              </div>
            ) : (
              <div className="space-y-3 text-sm" data-testid="service-contact-block">
                <div>
                  <div className="text-[10px] font-mono uppercase tracking-wider opacity-60">Requester</div>
                  <div className="font-medium">{s.requester_full_name}</div>
                  {(s.requester_city || s.requester_country) && (
                    <div className="text-xs opacity-70">{[s.requester_city, s.requester_country].filter(Boolean).join(", ")}</div>
                  )}
                </div>

                <div className="flex items-center gap-2">
                  <Mail className="w-4 h-4 opacity-60" />
                  <a
                    href={`mailto:${s.requester_email}?subject=Social Pulse — ${encodeURIComponent(s.title)}`}
                    className="underline break-all"
                    style={{ color: "var(--wofy-primary)" }}
                    data-testid="service-email-link"
                  >
                    {s.requester_email}
                  </a>
                  <button onClick={() => copy(s.requester_email)} aria-label="Copy email" className="opacity-60 hover:opacity-100">
                    <Copy className="w-3.5 h-3.5" />
                  </button>
                </div>

                {s.requester_phone && (
                  <div className="flex items-center gap-2">
                    <Phone className="w-4 h-4 opacity-60" />
                    <a href={`tel:${s.requester_phone}`} className="underline" style={{ color: "var(--wofy-primary)" }}>
                      {s.requester_phone}
                    </a>
                    <button onClick={() => copy(s.requester_phone)} aria-label="Copy phone" className="opacity-60 hover:opacity-100">
                      <Copy className="w-3.5 h-3.5" />
                    </button>
                  </div>
                )}
                <div className="text-[11px] opacity-70">
                  Prefers <strong>{s.preferred_contact}</strong>.
                </div>
              </div>
            )}
          </div>

          {/* Timing */}
          {(s.preferred_date || s.recurring) && (
            <div className="wofy-card">
              <div className="overline mb-2 inline-flex items-center gap-1.5">
                <Calendar className="w-3.5 h-3.5" /> TIMING
              </div>
              {s.preferred_date && (
                <div className="text-sm">
                  Preferred date: <strong>{s.preferred_date}</strong>
                  {s.flexible_dates ? <span className="opacity-70"> · flexible</span> : null}
                </div>
              )}
              {s.recurring && (
                <div className="text-sm mt-1">
                  Recurring · <em>{s.recurring_pattern || "annual"}</em>
                </div>
              )}
            </div>
          )}

          {/* Offering */}
          {(s.budget_range || s.offering) && (
            <div className="wofy-card">
              <div className="overline mb-2">OFFERING</div>
              {s.budget_range && <div className="text-sm"><strong>{s.budget_range}</strong></div>}
              {s.offering && <div className="text-sm mt-1 opacity-80">{s.offering}</div>}
            </div>
          )}

          {/* Requester meta */}
          <div className="text-[11px] opacity-60 px-2">
            Posted by {s.submitter_name || "a community member"}
            {s.created_at && ` · ${s.created_at.slice(0, 10)}`}
            {s.submitter_verified && <span className="ml-1">· ✓ verified contributor</span>}
          </div>
        </aside>
      </div>
    </div>
  );
}

function Row({ label, v, span, sensitive }) {
  if (!v) return null;
  return (
    <div className={span ? "sm:col-span-2" : ""}>
      <div className="text-[10px] font-mono uppercase tracking-wider opacity-60">
        {label}{sensitive && " · private"}
      </div>
      <div className="mt-0.5 whitespace-pre-line">{v}</div>
    </div>
  );
}
